import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--lightbox"
// Renders ONE dynamic Ui::ModalComponent that swaps its image / filename /
// download href / delete-form action based on which thumbnail the operator
// clicked. Avoids rendering N modals for N attachments.
//
// Wiring:
//   <div data-controller="ui--lightbox">
//     <button
//       data-action="click->ui--lightbox#open"
//       data-ui--lightbox-image-url-param="…"
//       data-ui--lightbox-download-url-param="…"
//       data-ui--lightbox-delete-url-param="…"
//       data-ui--lightbox-filename-param="…">
//       <img thumb />
//     </button>
//     <!-- Ui::ModalComponent renders a <dialog>, we target its content: -->
//     <dialog data-ui--lightbox-target="modal" …>
//       <img data-ui--lightbox-target="image" />
//       <p data-ui--lightbox-target="filename" />
//       <form data-ui--lightbox-target="deleteForm" action=""> … </form>
//       <a data-ui--lightbox-target="downloadLink" href="#" download> … </a>
//     </dialog>
//   </div>
//
// The Ui::ModalController on the <dialog> element owns show/hide;
// `open()` delegates to it via getControllerForElementAndIdentifier so
// we don't reimplement the dialog focus trap / backdrop.
export default class extends Controller {
  static targets = [
    "modal",
    "image",
    "documentPreview",
    "filename",
    "downloadLink",
    "deleteForm",
  ]

  connect() {
    // The <dialog> fires a native "close" event on any close path (the
    // modal's own close button, Escape, or a JS `.close()` call) — clear
    // both preview surfaces then so a hidden PDF iframe doesn't keep
    // rendering in the background until the NEXT open() resets it.
    if (this.hasModalTarget) {
      this.modalTarget.addEventListener("close", () => this._clearPreviews())
    }
  }

  _clearPreviews() {
    if (this.hasImageTarget) this.imageTarget.src = ""
    if (this.hasDocumentPreviewTarget) this.documentPreviewTarget.src = ""
  }

  open(event) {
    const params = event.params || {}
    const isDocument = params.mediaKind === "document"

    // Photo vs document — toggle which preview surface is visible. Documents
    // (PDFs) render inline via an <iframe> that hands the file to the
    // browser's native PDF viewer; photos render via <img>. The unused
    // surface has its src cleared so it stops loading in the background.
    if (this.hasImageTarget) {
      this.imageTarget.hidden = isDocument
      this.imageTarget.src = isDocument ? "" : params.imageUrl || ""
    }
    if (this.hasDocumentPreviewTarget) {
      this.documentPreviewTarget.hidden = !isDocument
      this.documentPreviewTarget.src = isDocument ? params.imageUrl || "" : ""
    }

    if (this.hasFilenameTarget)
      this.filenameTarget.textContent = params.filename || ""
    if (this.hasDownloadLinkTarget)
      this.downloadLinkTarget.href = params.downloadUrl || "#"
    if (this.hasDeleteFormTarget && params.deleteUrl) {
      this.deleteFormTarget.action = params.deleteUrl
    }

    if (this.hasModalTarget) {
      const modalController =
        this.application.getControllerForElementAndIdentifier(
          this.modalTarget,
          "ui--modal",
        )
      modalController?.open()
    }
  }
}
